import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { TagProps } from '../components/app/design/Tag'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { FKType } from './SQLBuilder'
import { Source } from './Source'
import { User } from './User'

type DepositConstructorArgs = ConstructorArgs<() => Deposit>

export class Deposit extends BaseRecord {
    readonly recordType = 'deposit'

    amount?: number
    total?: number
    fee?: number
    memo?: string
    refnum?: string
    lastModifiedDate?: moment.Moment
    cancelledDate?: moment.Moment
    status?: string
    paidDate?: moment.Moment

    constructor(data?: Partial<DepositConstructorArgs>) {
        super(data)
        if (!data) {
            this.owner = PSApplication.me()
            return
        }

        this.amount = this.propertyFromCurrencyArg(data.amount)
        this.total = this.propertyFromCurrencyArg(data.total)
        this.fee = this.propertyFromCurrencyArg(data.fee)
        this.memo = data.memo ?? undefined
        this.refnum = data.refnum ?? undefined
        this.status = data.status ?? undefined
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.paidDate = this.propertyFromTimestampArg(data.paid_date)
    }

    clone(overrides?: Partial<DepositConstructorArgs>): Deposit {
        return new Deposit({ ...this.serializeForClone(), ...overrides })
    }

    get sentDate(): moment.Moment | undefined {
        return this.paidDate !== undefined
            ? this.paidDate
            : this.status === 'paid'
              ? this.lastModifiedDate
              : undefined
    }

    get statusString(): string {
        if (this.cancelledDate) return 'Canceled'
        if (this.status === 'paid') return 'Received by bank'
        if (this.status === 'in_transit') return 'Sent to bank'
        return 'Fully processed'
    }

    get statuses(): {
        title: string
        date: moment.Moment | undefined
        intent: TagProps['intent']
    }[] {
        return [
            {
                title: 'Created',
                date: this.createdDate,
                intent: 'info',
            },
            {
                title: 'Sent',
                date: this.sentDate,
                intent: 'info',
            },
            {
                title: 'Received',
                date: this.paidDate,
                intent: 'success',
            },
        ]
    }

    get statusTag(): { label: string; intent: TagProps['intent'] } | null {
        const lastActiveStatus = this.statuses.findLast((status) => status.date !== undefined)
        if (!lastActiveStatus) {
            return null
        }

        return { label: lastActiveStatus.title, intent: lastActiveStatus.intent }
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
    }

    static fk: FKType = {
        users: ['owner'],
        sources: ['source'],
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['owner'] = this.owner?.id
        dict['source'] = this.source?.id
        dict['amount'] = this.amount
        dict['total'] = this.total
        dict['fee'] = this.fee
        if (this.memo) dict['memo'] = this.memo
        return dict
    }

    static dataType(): DataType {
        return 'deposits'
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get source(): Source | undefined {
        return this.getForeignRecord('source', Source)
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
}
